import {useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {InputText} from '../../../../../_metronic/elements/Inputs'
import {sortStrings} from '../../../../shared/optionSort'

const provinces = [
  'Buenos Aires',
  'Ciudad Autónoma de Buenos Aires',
  'Catamarca',
  'Chaco',
  'Chubut',
  'Córdoba',
  'Corrientes',
  'Entre Rios',
  'Formosa',
  'Jujuy',
  'La Pampa',
  'La Rioja',
  'Mendoza',
  'Misiones',
  'Neuquén',
  'Rio Negro',
  'Salta',
  'San Juan',
  'San Luis',
  'Santa Cruz',
  'Santa Fe',
  'Santiago del Estero',
  'Tierra del Fuego, Antártida e Islas del Atlántico Sur',
  'Tucumán',
]

const ProvinceSelect = ({disabled, formik, label, name, options, placeholder}) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)
  const value = formik.values[name] || ''
  const selectedLabel = value || placeholder
  const sortedOptions = useMemo(() => sortStrings(options), [options])

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false)
      }
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const selectOption = (option) => {
    formik.setFieldValue(name, option)
    formik.setFieldTouched(name, true, false)
    setOpen(false)
  }

  return (
    <div className={`specific-account-select ${open ? 'is-open' : ''}`.trim()} ref={containerRef}>
      <button
        type='button'
        id={name}
        disabled={disabled}
        className='specific-account-select__trigger'
        aria-expanded={open}
        aria-haspopup='listbox'
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className='specific-account-select__label'>{label}</span>
        <span className={`specific-account-select__value ${!value ? 'is-placeholder' : ''}`.trim()}>
          {selectedLabel}
        </span>
        <span className='specific-account-select__chevron' aria-hidden='true' />
      </button>

      {open && (
        <div className='specific-account-select__menu' role='listbox' aria-label={label}>
          {sortedOptions.map((option) => (
            <button
              type='button'
              role='option'
              aria-selected={value === option}
              className={`specific-account-select__option ${
                value === option ? 'active' : ''
              }`.trim()}
              key={option}
              onClick={() => selectOption(option)}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const UpdateAccountInformationInputs = ({formik}) => {
  const {t} = useTranslation()
  const fields = [
    {
      name: 'first_name',
      label: t('account:update.fields.first_name.label'),
      placeholder: t('account:update.fields.first_name.placeholder'),
    },
    {
      name: 'last_name',
      label: t('account:update.fields.last_name.label'),
      placeholder: t('account:update.fields.last_name.placeholder'),
    },
    {
      name: 'email',
      label: t('account:update.fields.email.label'),
      placeholder: t('account:update.fields.email.placeholder'),
      disabled: true,
    },
    {
      name: 'company_name',
      label: t('account:update.fields.company_name.label'),
      placeholder: t('account:update.fields.company_name.placeholder'),
    },
    {
      name: 'phone',
      label: t('account:update.fields.phone.label'),
      placeholder: t('account:update.fields.phone.placeholder'),
    },
    {
      name: 'province',
      type: 'province',
      label: t('account:update.fields.state.label'),
      placeholder: t('account:update.fields.state.default_option'),
    },
    {
      name: 'city',
      label: t('account:update.fields.city.label'),
      placeholder: t('account:update.fields.city.placeholder'),
    },
    {
      name: 'address',
      label: t('account:update.fields.address.label'),
      placeholder: t('account:update.fields.address.placeholder'),
    },
    {
      name: 'zip_code',
      label: t('account:update.fields.zip_code.label'),
      placeholder: t('account:update.fields.zip_code.placeholder'),
    },
  ]

  return (
    <div className='specific-account-edit-list'>
      {fields.map((field) => (
        <div className='specific-account-edit-row' key={field.name}>
          <label className='specific-account-edit-row__label' htmlFor={field.name}>
            {field.label}
          </label>
          <div className='specific-account-edit-row__control'>
            {field.type === 'province' ? (
              <ProvinceSelect
                disabled={formik.isSubmitting}
                formik={formik}
                label={field.label}
                name={field.name}
                options={provinces}
                placeholder={field.placeholder}
              />
            ) : (
              <InputText
                formik={formik}
                name={field.name}
                inputProps={{
                  disabled: formik.isSubmitting || field.disabled,
                  id: field.name,
                  placeholder: field.placeholder,
                }}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default UpdateAccountInformationInputs
